import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Compass, Cat, Gift, BookHeart, LogOut, Coins, Trophy } from "lucide-react";
import { useApp } from "../context/AppState";
import { useAuth } from "../context/AuthContext";
import { HOLIDAYS, holidayProgress, activeHolidayId } from "../data/holidays";

const NAV = [
  { to: "/", label: "Jelajah", short: "Jelajah", icon: Compass, end: true },
  { to: "/mochi", label: "Mochi kita", short: "Mochi", icon: Cat },
  { to: "/hadiah", label: "Hadiah", short: "Hadiah", icon: Gift },
  { to: "/peringkat", label: "Peringkat", short: "Peringkat", icon: Trophy },
  { to: "/cerita", label: "Cerita kita", short: "Cerita", icon: BookHeart },
];

function PointsPill({ points }) {
  return (
    <div
      className="flex items-center gap-2 rounded-full border border-[#eee1cf] bg-[#fffaf0] px-3 py-1.5 text-[13px] font-bold text-[#8a6b3d]"
      data-testid="points-badge"
    >
      <Coins className="h-4 w-4 text-[#e0a94b]" />
      <span>{Number(points).toLocaleString("id-ID")}</span>
      <span className="font-medium text-[#b09a7d]">poin</span>
    </div>
  );
}

export default function Layout({ children }) {
  const { state } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const points = state?.points ?? 0;
  const initials = (state?.coupleName || "Kita")
    .split("&").map((s) => s.trim()[0]).filter(Boolean).slice(0, 2).join("");

  const activeH = activeHolidayId();
  const doneCount = holidayProgress(state);
  const claimedH = state?.holidaysClaimed || [];
  const holidayAlert = HOLIDAYS.some(
    (h) => !claimedH.includes(h.id) && (activeH === h.id || doneCount >= h.mission.target)
  );

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Desktop / top nav */}
      <header className="sticky top-0 z-40 border-b border-[#efeae4] bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[22px] font-extrabold tracking-tight"
            data-testid="brand-logo"
          >
            <span className="grid h-8 w-8 place-items-center rounded-xl kk-coral-gradient text-white">
              <BookHeart className="h-4 w-4" />
            </span>
            <span>Kencan<span className="text-[#ff5a5f]">Kita</span></span>
          </button>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.short.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
                    isActive ? "bg-[#ffebeb] text-[#ff5a5f]" : "text-[#7c7277] hover:text-[#1e1b18]"
                  }`
                }
              >
                <span className="relative">
                  <n.icon className="h-4 w-4" />
                  {n.to === "/hadiah" && holidayAlert && (
                    <span data-testid="holiday-alert-dot" className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#ff3b30] ring-2 ring-white" />
                  )}
                </span>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <PointsPill points={points} />
            <div className="hidden items-center gap-2 sm:flex">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[#ffe1dd] text-xs font-bold text-[#c9566b]">
                {initials || "K"}
              </span>
              {user?.role === "admin" && (
                <Link to="/admin" data-testid="admin-link" className="hidden items-center gap-1 rounded-full bg-[#1f1b18] px-3 py-1.5 text-[12px] font-bold text-white sm:flex">Admin</Link>
              )}
              <button
                onClick={doLogout}
                title="Keluar"
                data-testid="logout-button"
                className="grid h-9 w-9 place-items-center rounded-full bg-[#f7f3ef] text-[#8a7a80] hover:bg-[#efe4df]"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 md:pb-16">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#efeae4] bg-white/95 px-2 pb-[env(safe-area-inset-bottom)] pt-1.5 backdrop-blur-lg md:hidden">
        <div className="mx-auto flex max-w-md items-stretch justify-around">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`bottomnav-${n.short.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-0.5 rounded-2xl py-2 text-[10px] font-semibold transition-colors ${
                  isActive ? "text-[#ff5a5f]" : "text-[#9a9089]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`grid h-8 w-12 place-items-center rounded-full transition-colors ${isActive ? "bg-[#ffebeb]" : ""}`}>
                    <span className="relative">
                      <n.icon className="h-[18px] w-[18px]" />
                      {n.to === "/hadiah" && holidayAlert && (
                        <span data-testid="holiday-alert-dot-mobile" className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#ff3b30] ring-2 ring-white" />
                      )}
                    </span>
                  </span>
                  {n.short}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
